import React from 'react'
import { Table, Alert, Button } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import ScreenshotPlaceholder from '../components/ScreenshotPlaceholder'
import { downloadSampleTemplate } from './sample_csv/sampleTemplates'

/**
 * helpArticles.jsx — content for the in-platform Documentation page.
 *
 * Content is English only (Phase 1). To translate later, duplicate this file
 * per language and have Documentation.jsx pick the right one — no other
 * code changes are needed.
 *
 * Screenshots: wherever <ScreenshotPlaceholder> appears, a dashed box tells
 * the content author exactly what to capture and the filename to use. Drop
 * the PNG into frontend/public/help-images/ and it replaces the placeholder.
 */

// --- small presentational helpers -------------------------------------------
const TemplateBtn = ({ type, children }) => (
  <Button variant="outline-success" size="sm" className="me-2 mb-2"
    onClick={() => downloadSampleTemplate(type)}>
    <span className="me-1">⬇</span>{children}
  </Button>
)
const Tip = ({ children }) => (
  <Alert variant="success" className="py-2 my-3" style={{ fontSize: '0.9rem' }}>
    <strong>💡 Tip:</strong> {children}
  </Alert>
)
const Warn = ({ children }) => (
  <Alert variant="warning" className="py-2 my-3" style={{ fontSize: '0.9rem' }}>
    <strong>⚠️ Watch out:</strong> {children}
  </Alert>
)
const Code = ({ children }) => (
  <code style={{ background: 'var(--color-neutral-100)', padding: '1px 5px', borderRadius: 4 }}>{children}</code>
)
const ArticleLink = ({ to, children }) => (
  <Link to={`/documentation?a=${to}`} style={{ color: 'var(--color-primary-dark)', fontWeight: 600 }}>{children}</Link>
)
const Ext = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer"
    style={{ color: 'var(--color-primary-dark)', fontWeight: 600, textDecoration: 'underline' }}>
    {children || href}
  </a>
)

// --- categories (sidebar grouping) ------------------------------------------
export const HELP_CATEGORIES = [
  { id: 'getting-started', label: 'Getting Started', icon: '🚀' },
  { id: 'data', label: 'Preparing & Uploading Data', icon: '📁' },
  { id: 'analysis', label: 'Running an Analysis', icon: '▶️' },
  { id: 'results', label: 'Understanding Results', icon: '📊' },
  { id: 'troubleshooting', label: 'Troubleshooting', icon: '🛠️' },
]

// --- articles ----------------------------------------------------------------
export const HELP_ARTICLES = [

  // =========================================================================
  {
    id: 'welcome',
    category: 'getting-started',
    title: 'Welcome — how the platform works',
    summary: 'A quick tour of what the platform does and the steps to get a result.',
    body: (
      <>
        <p>
          The EcoEmpower platform helps you plan and manage the energy of your community —
          forecasting how much energy buildings will use, optimising when to use it, and
          estimating the costs and benefits of investments such as solar panels or batteries.
        </p>
        <p>Working with the platform follows five simple steps:</p>
        <ol>
          <li><strong>Create a community</strong> — give it a name, country and currency.</li>
          <li><strong>Add buildings and assets</strong> — the buildings in your community and any
            equipment such as solar panels, batteries or EV chargers.</li>
          <li><strong>Upload data</strong> — historical energy files (CSV files) describing how
            much energy each building uses, how much your solar/wind produces, and electricity prices.</li>
          <li><strong>Run the tools</strong> — Forecasting, Optimisation (Modelling) and Long-Term Planning.</li>
          <li><strong>View results</strong> — charts and figures you can save and export.</li>
        </ol>
        <Tip>
          The tools work best with real data, but you can explore the platform before you have it —
          most tools fall back to illustrative sample results so nothing is blocked.
        </Tip>
        <ScreenshotPlaceholder
          img="dashboard-overview.png"
          description="The main Dashboard after logging in, showing the community count cards and the three action cards (Communities, Quick Start, Documentation)."
          source="Platform — log in and capture the main Dashboard (the page at the root URL, e.g. http://localhost/)."
          caption="The Dashboard is your starting point."
        />
        <h5 className="mt-4">Where data fits in</h5>
        <p>
          The single most important — and most common — task is uploading your data files correctly.
          The <strong>Preparing &amp; Uploading Data</strong> section of this guide explains exactly
          how to do that, with ready-to-use templates. If an upload is ever rejected, the
          <strong> Troubleshooting</strong> section explains every error message in plain language.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-overview',
    category: 'data',
    title: 'What data files do I need?',
    summary: 'The full list of data files, what each one is for, and when you need it.',
    body: (
      <>
        <p>
          Data is uploaded as <strong>CSV files</strong> — simple spreadsheet files you can
          create in Excel or Google Sheets. There are two groups:
        </p>
        <ul>
          <li><strong>Building data</strong> — uploaded for a specific building (its energy use, its rooftop solar, its bills).</li>
          <li><strong>Community data</strong> — uploaded once for the whole community (a shared solar farm, wind turbine, electricity prices, weather).</li>
        </ul>

        <h5 className="mt-4">Building data files</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>File</th><th>Columns (required)</th><th>What it is for</th></tr>
          </thead>
          <tbody>
            <tr><td>Building Load Profile</td><td><Code>timestamp</Code>, <Code>consumption_kwh</Code></td><td>How much electricity the building uses each hour — needed for forecasting and optimisation.</td></tr>
            <tr><td>Building PV Generation</td><td><Code>timestamp</Code>, <Code>generation_kwh</Code></td><td>How much the building's own rooftop solar produced each hour.</td></tr>
            <tr><td>Building Energy Bill</td><td><Code>period_start</Code>, <Code>period_end</Code>, <Code>consumption_kwh</Code>, <Code>cost_eur</Code></td><td>Monthly bills — used for cost analysis.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Community data files</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>File</th><th>Columns (required)</th><th>What it is for</th></tr>
          </thead>
          <tbody>
            <tr><td>PV Generation Profile</td><td><Code>timestamp</Code>, <Code>generation_kw</Code></td><td>Output of a community-shared solar farm.</td></tr>
            <tr><td>Wind Generation Profile</td><td><Code>timestamp</Code>, <Code>generation_kw</Code></td><td>Output of a community wind turbine.</td></tr>
            <tr><td>Historical Import Tariff</td><td><Code>timestamp</Code>, <Code>import_price_eur_kwh</Code></td><td>The price you paid to buy electricity, hour by hour, over a year.</td></tr>
            <tr><td>Historical Export Tariff</td><td><Code>timestamp</Code>, <Code>export_price_eur_kwh</Code></td><td>The price you were paid to sell electricity back to the grid.</td></tr>
            <tr><td>Day-Ahead Import / Export Tariff</td><td>same as above</td><td>Tomorrow's hourly prices (24 rows) — uploaded each day for day-ahead optimisation.</td></tr>
            <tr><td>Solar Weather</td><td><Code>timestamp</Code>, <Code>ghi_w_m2</Code></td><td>Sunlight and temperature — improves solar forecasts.</td></tr>
            <tr><td>Wind Weather</td><td><Code>timestamp</Code>, <Code>wind_speed_m_s</Code></td><td>Wind speed — improves wind forecasts.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Where does this data come from?</h5>
        <p>You usually do not type this data by hand — you export it from a system you already use:</p>
        <ul>
          <li><strong>Building consumption</strong> — from the building's smart-meter online portal,
            from your distribution system operator (DSO), or from your electricity supplier's
            customer account.</li>
          <li><strong>Solar / wind generation</strong> — from the monitoring app or web portal of the
            inverter or turbine (usually set up by the installer).</li>
          <li><strong>Tariffs</strong> — from your electricity contract or supplier; day-ahead prices
            come from the national power exchange or, again, your supplier.</li>
          <li><strong>Weather</strong> — from a national meteorological service or a free
            weather-data provider. This file is optional.</li>
        </ul>
        <p>
          These systems usually let you export a chosen date range as a CSV or Excel file. Once you
          have that file, the next two articles show how to get it into the right shape.
        </p>

        <Tip>
          You do <strong>not</strong> need every file to start. The essentials are a
          <strong> Building Load Profile</strong> for each building and a <strong>tariff</strong> file.
          Add generation and weather files later to improve accuracy.
        </Tip>
        <p className="mt-3">
          Each file type has its own short article in this section, with an example and a
          downloadable template. Start with <em>“Creating CSV files correctly”</em> below — it
          prevents the large majority of upload problems.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-csv-basics',
    category: 'data',
    title: 'Creating CSV files correctly (Excel & Google Sheets)',
    summary: 'The golden rules for a valid file — and the number-one mistake to avoid.',
    body: (
      <>
        <p>
          A <strong>CSV file</strong> is just a spreadsheet saved in a simple text format.
          You prepare it in Excel or Google Sheets like any spreadsheet, then save it as CSV.
          Follow these rules and your uploads will work the first time.
        </p>

        <h5 className="mt-4">The golden rules</h5>
        <ol>
          <li><strong>First row = column headings.</strong> The first row must contain the exact
            column names for the file type (for example <Code>timestamp</Code> and <Code>consumption_kwh</Code>).
            Spelling and lower-case letters must match exactly.</li>
          <li><strong>One row per hour.</strong> Each following row is one hour of data. A full
            year is <strong>8,760 rows</strong> (365 days × 24 hours), plus the heading row.</li>
          <li><strong>Use the timestamp format</strong> <Code>2025-01-01T14:00:00Z</Code> — that is
            Year-Month-Day, the letter <Code>T</Code>, then Hour:Minute:Second, then the letter <Code>Z</Code>.</li>
          <li><strong>Numbers use a dot, never a comma.</strong> Write <Code>3.5</Code>, not <Code>3,5</Code>.</li>
          <li><strong>No empty rows, no totals, no extra notes</strong> — only the heading row and data rows.</li>
        </ol>

        <Warn>
          <strong>The most common mistake.</strong> In Italy, France, Germany, Czechia and Greece,
          spreadsheet programs normally write decimal numbers with a <strong>comma</strong>
          (<Code>0,457</Code>). The platform expects a <strong>dot</strong> (<Code>0.457</Code>), so
          a comma file is rejected with “Value must be a number”. If your file already has commas,
          you do <strong>not</strong> need to retype it — see{' '}
          <ArticleLink to="data-fix-commas">Fixing a file that uses commas for decimals</ArticleLink>.
        </Warn>

        <h5 className="mt-4">About the timestamp column</h5>
        <p>
          The most reliable format is <Code>2025-01-01T14:00:00Z</Code>, and it has a hidden
          advantage: Excel tends to silently re-format anything it recognises as a date, but it
          usually leaves this form alone because of the <Code>T</Code> and <Code>Z</Code>. The
          platform also understands common European formats such as <Code>01/01/2025 14:00</Code>
          and <Code>2025-01-01</Code> — but those are exactly the ones Excel is likely to change
          behind your back.
        </p>
        <p>
          If your timestamps were mangled, or you need to build the column, put this formula in a
          spare column (here the original date/time is in cell <Code>A2</Code>):
        </p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`=TEXT(A2,"yyyy-mm-ddThh:mm:ss")&"Z"`}
        </pre>
        <p>
          Copy it down every row, then select the column and use
          <strong> Copy → Paste Special → Values</strong> to lock in the result. Use that column as
          your <Code>timestamp</Code>.
        </p>

        <h5 className="mt-4">How many rows? (data resolution)</h5>
        <p>
          One row per time period. <strong>Hourly</strong> data is typical — a full year is
          <strong> 8,760 rows</strong> — but <strong>15-minute</strong> and <strong>daily</strong>
          data are accepted too. A minimum of 24 rows (one day) is needed.
        </p>

        <h5 className="mt-4">Saving as CSV from Microsoft Excel</h5>
        <ol>
          <li>Click <strong>File → Save As</strong>.</li>
          <li>In the file-type dropdown choose <strong>“CSV UTF-8 (Comma delimited) (*.csv)”</strong>.</li>
          <li>Click <strong>Save</strong>. If Excel warns that some features will be lost, click <strong>Yes</strong> — that is expected.</li>
        </ol>
        <p className="small text-muted">
          If your version of Excel does not list “CSV UTF-8”, choose “CSV (Comma delimited)” instead.
        </p>
        <ScreenshotPlaceholder
          img="excel-save-as-csv.png"
          description="Excel's Save As dialog with the file-type dropdown open, highlighting the 'CSV UTF-8 (Comma delimited)' option."
          source="Microsoft Excel — open any workbook → File → Save As → click the file-type dropdown. Capture the dropdown open with the CSV UTF-8 entry highlighted."
          caption="In Excel, always choose “CSV UTF-8”."
        />

        <h5 className="mt-4">Saving as CSV from Google Sheets</h5>
        <ol>
          <li>Click <strong>File → Download → Comma-separated values (.csv)</strong>.</li>
          <li>The file is saved to your computer's Downloads folder.</li>
        </ol>
        <ScreenshotPlaceholder
          img="gsheets-download-csv.png"
          description="Google Sheets File → Download submenu, highlighting 'Comma-separated values (.csv)'."
          source="Google Sheets (sheets.google.com) — open any spreadsheet → File → Download. Capture the submenu open with 'Comma-separated values (.csv)' highlighted."
          caption="In Google Sheets, use File → Download → .csv."
        />

        <h5 className="mt-4">Check your file before uploading</h5>
        <p>
          Open the saved <Code>.csv</Code> file once more and confirm the points below. To be
          completely sure, open it with a plain text editor (<strong>Notepad</strong> on Windows,
          <strong> TextEdit</strong> on Mac) rather than a spreadsheet — a text editor shows the
          file's true contents, while Excel re-applies your local formatting on screen and can hide
          the real problem.
        </p>
        <p>Confirm that:</p>
        <ul>
          <li>The first row shows the correct column names.</li>
          <li>Numbers look like <Code>3.5</Code> (dot), not <Code>3,5</Code> (comma).</li>
          <li>Timestamps look like <Code>2025-01-01T14:00:00Z</Code>.</li>
          <li>There are no blank rows or summary lines at the bottom.</li>
        </ul>
        <Tip>
          Easiest of all: download a template from any of the file-specific articles in this
          section, replace the example numbers with your own, and save. The template already has
          the correct headings and number format.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-fix-commas',
    category: 'data',
    title: 'Fixing a file that uses commas for decimals',
    summary: 'Convert an existing comma-number file (3,5) into the dot format the platform needs (3.5) — without retyping anything.',
    body: (
      <>
        <p>
          Spreadsheet programs in Italy, France, Germany, Czechia and Greece normally write
          decimal numbers with a <strong>comma</strong> — <Code>0,457</Code> instead of
          {' '}<Code>0.457</Code>. The platform needs the dot form. The good news: you do
          {' '}<strong>not</strong> need to retype anything. Change one setting, save the file
          again, and you are done. Use whichever method matches the program you have.
        </p>

        <h5 className="mt-3">Method 1 — Google Sheets (easiest, free)</h5>
        <ol>
          <li>Go to <strong>sheets.google.com</strong> and open a blank spreadsheet.</li>
          <li>Choose <strong>File → Import → Upload</strong> and select your CSV file.</li>
          <li>Choose <strong>File → Settings</strong>. Under <strong>Locale</strong>, select
            {' '}<strong>United States</strong> (or United Kingdom), then <strong>Save settings</strong>.
            This tells Google Sheets to use a dot for decimals.</li>
          <li>Choose <strong>File → Download → Comma-separated values (.csv)</strong>.</li>
        </ol>
        <p>The downloaded file now uses dots and is ready to upload.</p>
        <ScreenshotPlaceholder
          img="gsheets-locale-setting.png"
          description="Google Sheets — the File → Settings dialog with the Locale dropdown open and 'United States' highlighted."
          source="Google Sheets (sheets.google.com) — open any spreadsheet → File → Settings → the General tab. Capture the modal with the Locale dropdown open and 'United States' highlighted."
          caption="Setting the Locale to United States makes Google Sheets export dots."
        />

        <h5 className="mt-4">Method 2 — Microsoft Excel</h5>
        <ol>
          <li>Open the file in Excel, then go to <strong>File → Options → Advanced</strong>.</li>
          <li>Under <strong>Editing options</strong>, untick <strong>“Use system separators”</strong>.</li>
          <li>Set <strong>Decimal separator</strong> to a dot <Code>.</Code> and
            {' '}<strong>Thousands separator</strong> to a single space (or leave it blank).</li>
          <li>Click <strong>OK</strong>, then use <strong>File → Save As</strong> and choose
            {' '}<strong>“CSV UTF-8 (Comma delimited)”</strong>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="excel-separator-options.png"
          description="Excel — File → Options → Advanced, the 'Editing options' area, with 'Use system separators' unticked and the decimal/thousands separator boxes visible."
          source="Microsoft Excel — open any workbook → File → Options → Advanced tab → scroll to 'Editing options'. Capture the section with the 'Use system separators' checkbox unticked."
          caption="In Excel, turn off system separators and set the decimal separator to a dot."
        />
        <Tip>This setting affects all of Excel. You can switch “Use system separators” back on afterwards.</Tip>

        <h5 className="mt-4">Method 3 — Find &amp; Replace (last resort)</h5>
        <p>
          Only use this if the numbers are stored as plain text and the file has <strong>no</strong>
          {' '}thousands separators (nothing in the <Code>1.234,5</Code> style), because a blind
          replace would also damage those.
        </p>
        <ol>
          <li>Select only the columns that contain numbers.</li>
          <li>Open <strong>Find &amp; Replace</strong> (Ctrl+H), find <Code>,</Code>, replace with
            {' '}<Code>.</Code>, then Replace All.</li>
          <li>Check a few values before saving.</li>
        </ol>

        <h5 className="mt-4">What about online converter tools?</h5>
        <p>
          Free online “CSV converter” websites exist, but they require uploading your file to a
          third-party site, and energy data can be sensitive. We recommend the methods above
          instead — they use software you already have and keep the file on your own computer. If
          your organisation already uses Google Workspace, Method 1 stays within tools you trust.
        </p>

        <Warn>
          A <Code>.csv</Code> file opened in Excel can <em>look</em> like it still has commas even
          when it was saved with dots, because Excel re-applies your local formatting on screen. To
          see what is <strong>really</strong> in the file, open it with a plain text editor —
          {' '}<strong>Notepad</strong> on Windows or <strong>TextEdit</strong> on Mac. There, the
          numbers should read <Code>3.5</Code>.
        </Warn>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-upload-how',
    category: 'data',
    title: 'Uploading a file, step by step',
    summary: 'How to upload a CSV and read the validation result.',
    body: (
      <>
        <p>Once your CSV file is ready, upload it from inside the community:</p>
        <ol>
          <li>Open your community and choose <strong>Data</strong> in the left-hand menu.</li>
          <li>In <strong>Select dataset type</strong>, pick the kind of file you are uploading
            (for example “Building Load Profile”).</li>
          <li>If asked, choose which <strong>building</strong> the file belongs to.</li>
          <li>Drag your CSV file onto the upload area, or click <strong>Browse files</strong>.</li>
          <li>Click <strong>Validate &amp; Preview</strong>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="upload-page.png"
          description="The community Data page with a dataset type selected, showing the required-columns badges, the download-template button, and the drag-and-drop upload area."
          source="Platform — open any community → click 'Data Management' in the sidebar → pick a dataset type from the dropdown so the upload area appears. Capture the form area."
          caption="The Data page: pick a type, then drop your file."
        />
        <h5 className="mt-4">Reading the result</h5>
        <ul>
          <li><strong>Green banner</strong> — the file is valid. It shows how many rows were read and the date range. You are done.</li>
          <li><strong>Yellow warnings</strong> — the file was accepted but something is worth checking (for example missing hours). You can keep it or improve the file.</li>
          <li><strong>Red banner</strong> — the file was rejected. The preview lists each problem with its row number. Fix the file and upload again.</li>
        </ul>
        <ScreenshotPlaceholder
          img="upload-validation-result.png"
          description="The validation result panel after a successful upload — green success banner plus the data preview table."
          source="Platform — on the same Data Management page, after uploading a valid CSV and clicking 'Validate & Preview'. Capture the green banner and the first few rows of the preview table."
          caption="A successful upload shows a green banner and a preview."
        />
        <p>
          Every red error message is explained in
          <em> “Why did my upload fail?”</em> in the Troubleshooting section.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-load',
    category: 'data',
    title: 'Building consumption (load) files',
    summary: 'The hourly electricity-use file — the most important file for each building.',
    body: (
      <>
        <p>
          A <strong>Building Load Profile</strong> records how much electricity a building used,
          one value per hour. It is the foundation for forecasting and optimisation, so prepare
          one for every building if you can.
        </p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Column</th><th>Required?</th><th>Meaning</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Required</td><td>The hour, e.g. <Code>2025-01-01T08:00:00Z</Code></td></tr>
            <tr><td><Code>consumption_kwh</Code></td><td>Required</td><td>Electricity used that hour, in kWh (a number, dot decimals, never negative)</td></tr>
            <tr><td><Code>peak_demand_kw</Code></td><td>Optional</td><td>Highest power draw in that hour, in kW</td></tr>
          </tbody>
        </Table>
        <p><strong>Example:</strong></p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,consumption_kwh
2025-01-01T00:00:00Z,0.457
2025-01-01T01:00:00Z,0.343
2025-01-01T02:00:00Z,0.298`}
        </pre>
        <p>One value per hour; a full year is 8,760 rows. At least 24 rows (one day) are needed.</p>
        <TemplateBtn type="building_load_profile">Download load template</TemplateBtn>
        <Warn>Consumption can never be negative. A negative value means a column was mixed up — check that generation has not been placed in the consumption column.</Warn>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-generation',
    category: 'data',
    title: 'Solar & wind generation files',
    summary: 'Hourly output of solar panels and wind turbines.',
    body: (
      <>
        <p>Generation files record how much energy a renewable source produced, one value per hour.</p>
        <h5 className="mt-3">Community solar farm or wind turbine</h5>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Column</th><th>Meaning</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>The hour, e.g. <Code>2025-01-01T12:00:00Z</Code></td></tr>
            <tr><td><Code>generation_kw</Code></td><td>Power produced that hour, in kW (never negative — at night solar is <Code>0</Code>)</td></tr>
          </tbody>
        </Table>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,generation_kw
2025-01-01T00:00:00Z,0
2025-01-01T12:00:00Z,105.6
2025-01-01T18:00:00Z,12.3`}
        </pre>
        <TemplateBtn type="pv_generation_profile">Solar (PV) template</TemplateBtn>
        <TemplateBtn type="wind_generation_profile">Wind template</TemplateBtn>

        <h5 className="mt-4">A building's own rooftop solar</h5>
        <p>
          For solar that belongs to a single building, the value column is
          <Code>generation_kwh</Code> (energy in kWh) instead of <Code>generation_kw</Code>.
          The platform shows the exact column name expected when you pick the dataset type on the
          upload page — always follow what it shows there.
        </p>
        <Tip>Hours with no sun or no wind should contain <Code>0</Code>, not be left blank.</Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-tariff',
    category: 'data',
    title: 'Tariff (electricity price) files',
    summary: 'Import and export electricity prices — historical and day-ahead.',
    body: (
      <>
        <p>Tariff files tell the platform the price of electricity for each hour.</p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>File</th><th>Value column</th><th>Rows</th></tr>
          </thead>
          <tbody>
            <tr><td>Historical Import Tariff</td><td><Code>import_price_eur_kwh</Code></td><td>One year, hourly (≈8,760)</td></tr>
            <tr><td>Historical Export Tariff</td><td><Code>export_price_eur_kwh</Code></td><td>One year, hourly (≈8,760)</td></tr>
            <tr><td>Day-Ahead Import Tariff</td><td><Code>import_price_eur_kwh</Code></td><td>24 (one day)</td></tr>
            <tr><td>Day-Ahead Export Tariff</td><td><Code>export_price_eur_kwh</Code></td><td>24 (one day)</td></tr>
          </tbody>
        </Table>
        <p>
          <strong>Import</strong> price is what you pay to buy electricity; <strong>export</strong>
          price is what you are paid to sell it. Prices are in euros per kWh (e.g. <Code>0.22</Code>)
          and use a dot decimal.
        </p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,import_price_eur_kwh
2025-01-01T00:00:00Z,0.08
2025-01-01T12:00:00Z,0.22
2025-01-01T19:00:00Z,0.28`}
        </pre>
        <TemplateBtn type="energy_tariff_data">Import tariff template</TemplateBtn>
        <TemplateBtn type="feed_in_tariff_data">Export tariff template</TemplateBtn>
        <Tip>
          <strong>Day-ahead</strong> files cover just one day (24 rows). Upload a new one each day —
          its timestamps should match that day's date. The templates above also work as a 24-row
          format reference.
        </Tip>

        <h5 className="mt-4">Where can I get tariff data?</h5>
        <p>
          Two different things are called "tariff" — handle them separately:
        </p>
        <ul>
          <li><strong>Wholesale / day-ahead prices</strong> — the hourly market price published by
            your country's power exchange. Use this for <em>day-ahead</em> tariff files and as a
            close approximation of variable contracts.</li>
          <li><strong>Retail tariff</strong> — what you actually pay (energy + grid fees + taxes).
            Take this from your supplier's contract or invoice.</li>
        </ul>

        <p className="mt-3"><strong>Pan-European source (works for every country):</strong></p>
        <ul>
          <li><strong>ENTSO-E Transparency Platform</strong> —
            {' '}<Ext href="https://transparency.entsoe.eu">transparency.entsoe.eu</Ext>. Free, but
            requires a free account. Open <em>Market → Day-ahead Prices</em>, pick your bidding
            zone (e.g. IT-North, FR, DE-LU, CZ, GR), the date range, and <em>Export Data</em> → CSV.
            The price column is in <strong>€/MWh — divide by 1000 to convert to €/kWh</strong>
            (e.g. 220 €/MWh = 0.22 €/kWh).</li>
        </ul>

        <p className="mt-3"><strong>National sources (often simpler, no account needed):</strong></p>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Country</th><th>Site</th><th>What you get</th></tr>
          </thead>
          <tbody>
            <tr><td>Italy</td><td><Ext href="https://www.mercatoelettrico.org">mercatoelettrico.org</Ext> (GME)</td><td>PUN day-ahead price, hourly. Look for "Esiti MGP". €/MWh — divide by 1000.</td></tr>
            <tr><td>France</td><td><Ext href="https://data.rte-france.com">data.rte-france.com</Ext> (RTE Open Data) or <Ext href="https://www.epexspot.com/en">epexspot.com</Ext></td><td>RTE: free open-data portal, hourly day-ahead prices. EPEX: market site.</td></tr>
            <tr><td>Germany</td><td><Ext href="https://www.smard.de/en">smard.de</Ext> (Federal Network Agency)</td><td>"Market data" → "Wholesale prices" → "Day-ahead, Germany/Luxembourg". Free download, no account.</td></tr>
            <tr><td>Czech Rep.</td><td><Ext href="https://www.ote-cr.cz/en">ote-cr.cz</Ext> (OTE)</td><td>"Short-term markets" → "Day-ahead market". CSV export per day.</td></tr>
            <tr><td>Greece</td><td><Ext href="https://www.enexgroup.gr/web/guest/markets-publications-el-day-ahead-market">enexgroup.gr</Ext> (HEnEx)</td><td>Day-Ahead Market results, daily PDFs and CSVs. €/MWh — divide by 1000.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Turning a downloaded price file into the platform's format</h5>
        <p>
          Power-exchange exports rarely come in the exact column names the platform wants. Use these
          steps to convert any of the files above:
        </p>
        <ol>
          <li>Open the downloaded CSV in Excel or Google Sheets.</li>
          <li>Identify the date/time column and the price column (other columns can be deleted).</li>
          <li>Make a column called <Code>timestamp</Code>. If the source already has an ISO
            timestamp, copy it; otherwise build one with the formula from
            {' '}<ArticleLink to="data-csv-basics">Creating CSV files correctly</ArticleLink>.</li>
          <li>Make a column called <Code>import_price_eur_kwh</Code>. If the source is €/MWh, divide
            by 1000 with a formula such as <Code>=B2/1000</Code> and paste-as-values.</li>
          <li>Save as <strong>CSV UTF-8</strong> and upload as <em>Historical Import Tariff</em>.</li>
        </ol>

        <Warn>
          Wholesale price ≠ retail price. The wholesale figure is the energy market price only and
          does not include grid fees, levies or VAT. For accurate cost analysis, prefer a retail
          tariff from your supplier, or combine the wholesale price with a known fixed adder.
        </Warn>

        <h5 className="mt-4">Export tariff (feed-in)</h5>
        <p>
          Export prices depend on your national scheme — they may be a flat feed-in tariff, the
          wholesale price minus a discount, or zero. Get the rule from your supplier, your DSO, or
          your national regulator (ARERA in Italy, CRE in France, BNetzA in Germany, ERÚ in Czechia,
          RAE in Greece) and write the resulting €/kWh value for each hour.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-weather',
    category: 'data',
    title: 'Weather files',
    summary: 'Optional files that make solar and wind forecasts more accurate.',
    body: (
      <>
        <p>
          Weather files are optional but improve forecast accuracy. There are two kinds.
        </p>
        <h5 className="mt-3">Solar weather</h5>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Column</th><th>Required?</th><th>Meaning</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Required</td><td>The hour</td></tr>
            <tr><td><Code>ghi_w_m2</Code></td><td>Required</td><td>Sunlight intensity, in watts per square metre</td></tr>
            <tr><td><Code>temperature_c</Code></td><td>Optional</td><td>Air temperature, °C</td></tr>
            <tr><td><Code>cloud_cover_pct</Code></td><td>Optional</td><td>Cloud cover, 0–100%</td></tr>
            <tr><td><Code>dni_w_m2</Code></td><td>Optional</td><td>Direct sunlight, W/m²</td></tr>
          </tbody>
        </Table>
        <TemplateBtn type="weather_solar">Solar weather template</TemplateBtn>

        <h5 className="mt-4">Wind weather</h5>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Column</th><th>Required?</th><th>Meaning</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Required</td><td>The hour</td></tr>
            <tr><td><Code>wind_speed_m_s</Code></td><td>Required</td><td>Wind speed, in metres per second</td></tr>
            <tr><td><Code>wind_direction_deg</Code></td><td>Optional</td><td>Wind direction, 0–360°</td></tr>
            <tr><td><Code>temperature_c</Code></td><td>Optional</td><td>Air temperature, °C</td></tr>
          </tbody>
        </Table>
        <TemplateBtn type="weather_wind">Wind weather template</TemplateBtn>
        <Tip>Optional columns can be left out entirely — but if you include a column, fill it for every row.</Tip>

        <h5 className="mt-4">Where can I get weather data?</h5>
        <p>
          You almost never need to record this yourself. Several free services publish hourly
          historical weather for any location in Europe. Start with the universal ones — they cover
          every pilot country and need no special account.
        </p>

        <h6 className="mt-3">Open-Meteo (recommended, easiest)</h6>
        <p>
          <Ext href="https://open-meteo.com/en/docs/historical-weather-api">open-meteo.com/en/docs/historical-weather-api</Ext>
          {' '}— free, no account, hourly history back to 1940.
        </p>
        <ol>
          <li>Open the page above. In the form, enter your community's <strong>latitude and
            longitude</strong> (find them on Google Maps — right-click the location → "What's here").</li>
          <li>Set <strong>Start date</strong> and <strong>End date</strong> for the year you want.</li>
          <li>Under <strong>Hourly Variables</strong>, tick the ones below (skip the wind list if
            you only need solar, and vice-versa):
            <ul>
              <li><Code>shortwave_radiation</Code> — this is GHI (in W/m²).</li>
              <li><Code>direct_radiation</Code> — this is DNI (optional).</li>
              <li><Code>temperature_2m</Code> — air temperature (°C).</li>
              <li><Code>cloud_cover</Code> — cloud cover (%).</li>
              <li><Code>wind_speed_10m</Code> — wind speed at 10 m (m/s).</li>
              <li><Code>wind_direction_10m</Code> — wind direction (degrees).</li>
            </ul>
          </li>
          <li>Set the format to <strong>CSV</strong> and click <strong>Download</strong>.</li>
          <li>Open the file. Rename the columns the platform expects:
            {' '}<Code>shortwave_radiation</Code> → <Code>ghi_w_m2</Code>,
            {' '}<Code>direct_radiation</Code> → <Code>dni_w_m2</Code>,
            {' '}<Code>temperature_2m</Code> → <Code>temperature_c</Code>,
            {' '}<Code>cloud_cover</Code> → <Code>cloud_cover_pct</Code>,
            {' '}<Code>wind_speed_10m</Code> → <Code>wind_speed_m_s</Code>,
            {' '}<Code>wind_direction_10m</Code> → <Code>wind_direction_deg</Code>.</li>
          <li>Make sure the timestamp column is named <Code>timestamp</Code>. Save as CSV UTF-8 and
            upload.</li>
        </ol>

        <h6 className="mt-3">PVGIS — European Commission JRC (best for solar planning)</h6>
        <p>
          <Ext href="https://re.jrc.ec.europa.eu/pvg_tools/en/">re.jrc.ec.europa.eu/pvg_tools/en/</Ext>
          {' '}— free, no account, covers all of Europe. Especially useful when you need a "typical
          year" reference (TMY) rather than a specific historical year.
        </p>
        <ol>
          <li>Open the site, pick your location on the map.</li>
          <li>Choose <strong>"Hourly data"</strong> in the left tabs.</li>
          <li>Tick <strong>Solar radiation components</strong> and <strong>2-m air temperature</strong>,
            select a year (or a range), and click <strong>Download CSV</strong>.</li>
          <li>The downloaded file has a small header you may need to delete so the column row
            (<Code>time</Code>, <Code>G(i)</Code>, <Code>T2m</Code>, …) is on row 1. Rename
            {' '}<Code>G(i)</Code> → <Code>ghi_w_m2</Code> and <Code>T2m</Code> → <Code>temperature_c</Code>.</li>
        </ol>
        <Tip>PVGIS times are usually in UTC and timestamps may look like <Code>20250101:1000</Code>
          — convert them to <Code>2025-01-01T10:00:00Z</Code> using the formula from
          {' '}<ArticleLink to="data-csv-basics">Creating CSV files correctly</ArticleLink>.</Tip>

        <h6 className="mt-3">Renewables.ninja (location-specific solar / wind output)</h6>
        <p>
          <Ext href="https://www.renewables.ninja">renewables.ninja</Ext> — free with a quick
          sign-up. Enter coordinates, choose "MERRA-2" or "ERA-5", year and resolution, and
          download CSV. The variable to map is <Code>irradiance</Code> → <Code>ghi_w_m2</Code> for
          solar, and <Code>wind_speed</Code> → <Code>wind_speed_m_s</Code> for wind.
        </p>

        <h6 className="mt-3">NASA POWER (global fallback)</h6>
        <p>
          <Ext href="https://power.larc.nasa.gov/data-access-viewer/">power.larc.nasa.gov/data-access-viewer</Ext>
          {' '}— global hourly data, free. Useful when other sources fail to cover the location.
          Variable names: <Code>ALLSKY_SFC_SW_DWN</Code> → <Code>ghi_w_m2</Code>,
          {' '}<Code>T2M</Code> → <Code>temperature_c</Code>,
          {' '}<Code>WS10M</Code> → <Code>wind_speed_m_s</Code>.
        </p>

        <h6 className="mt-3">National meteorological services</h6>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Country</th><th>Service</th></tr>
          </thead>
          <tbody>
            <tr><td>Italy</td><td>ISPRA / regional ARPA (e.g. ARPA Lombardia, ARPA Veneto) — hourly station data per region.</td></tr>
            <tr><td>France</td><td>Météo-France open-data portal (<Ext href="https://meteo.data.gouv.fr">meteo.data.gouv.fr</Ext>) — historical hourly observations.</td></tr>
            <tr><td>Germany</td><td>DWD Climate Data Center (<Ext href="https://opendata.dwd.de">opendata.dwd.de</Ext>) — free, station-level hourly data.</td></tr>
            <tr><td>Czech Rep.</td><td>CHMI (<Ext href="https://www.chmi.cz">chmi.cz</Ext>) — historical station data on request / via portal.</td></tr>
            <tr><td>Greece</td><td>HNMS / NOA-Meteo (<Ext href="https://www.meteo.gr">meteo.gr</Ext>) — limited free historical data; Open-Meteo is usually easier.</td></tr>
          </tbody>
        </Table>

        <Warn>
          Match the data <strong>year</strong> and <strong>time zone</strong> of your weather file
          to your other files. If your load data is for 2024 in local time, download 2024 weather in
          UTC and convert it (Open-Meteo lets you pick the time zone before downloading). Mismatched
          years or time zones make forecasts worse, not better.
        </Warn>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-bill',
    category: 'data',
    title: 'Energy bill files',
    summary: 'Monthly billing data used for cost analysis.',
    body: (
      <>
        <p>
          An <strong>Energy Bill</strong> file lists past bills, one row per billing period
          (usually one month). It is used for cost analysis.
        </p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Column</th><th>Required?</th><th>Meaning</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>period_start</Code></td><td>Required</td><td>First day of the bill, e.g. <Code>2025-01-01</Code></td></tr>
            <tr><td><Code>period_end</Code></td><td>Required</td><td>Last day of the bill, e.g. <Code>2025-01-31</Code></td></tr>
            <tr><td><Code>consumption_kwh</Code></td><td>Required</td><td>Total electricity used in the period, in kWh</td></tr>
            <tr><td><Code>cost_eur</Code></td><td>Required</td><td>Total amount paid, in euros</td></tr>
            <tr><td><Code>peak_demand_kw</Code></td><td>Optional</td><td>Highest power draw in the period, in kW</td></tr>
            <tr><td><Code>rate_eur_per_kwh</Code></td><td>Optional</td><td>Average price per kWh</td></tr>
          </tbody>
        </Table>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`period_start,period_end,consumption_kwh,cost_eur
2025-01-01,2025-01-31,3850,962.50
2025-02-01,2025-02-28,3520,880.00`}
        </pre>
        <TemplateBtn type="building_energy_bill">Download energy bill template</TemplateBtn>
        <Warn>Each period's start date must come before its end date, and periods should not overlap.</Warn>
      </>
    ),
  },

  // =========================================================================
  // RUNNING AN ANALYSIS
  // =========================================================================
  {
    id: 'analysis-walkthrough',
    category: 'analysis',
    title: 'From upload to result — a 15-minute walkthrough',
    summary: 'A single end-to-end run: create a community, upload one file, get a forecast and a first optimisation result.',
    body: (
      <>
        <p>
          This article ties everything together. Follow it once and you will have run every tool at
          least once. It assumes you have a single building's hourly consumption file ready.
        </p>

        <ol>
          <li>
            <strong>Create a community.</strong> Dashboard → <em>Communities</em> →
            {' '}<em>New community</em>. Give it a name and pick the country and currency. Save.
          </li>
          <li>
            <strong>Add at least one building.</strong> Open the community → <em>Buildings</em> →
            {' '}<em>Add building</em>. Building name, type and a postcode are enough to start.
          </li>
          <li>
            <strong>Upload your load file.</strong> Community → <em>Data</em> → pick
            {' '}<em>Building Load Profile</em>, choose the building, drop the CSV. See
            {' '}<ArticleLink to="data-upload-how">Uploading a file, step by step</ArticleLink> if
            the page is new to you.
          </li>
          <li>
            <strong>(Recommended) Upload a tariff file.</strong> Without prices, cost results will
            be illustrative only. See <ArticleLink to="data-tariff">Tariff files</ArticleLink>.
          </li>
          <li>
            <strong>Run a forecast.</strong> Community sidebar → <em>Forecasting</em>. The page has
            two tabs — <em>Short-Term (Day-Ahead)</em> and <em>Long-Term (Monthly)</em>. Pick a
            building, click <em>Run Forecast</em>. See
            {' '}<ArticleLink to="analysis-forecast">Running a forecast</ArticleLink>.
          </li>
          <li>
            <strong>Run an optimisation.</strong> Community sidebar → <em>Optimization</em> (the
            page itself is titled "Modelling"). Choose an objective (e.g. <em>Minimise cost</em>)
            and click <em>Run Optimization</em>. See
            {' '}<ArticleLink to="analysis-optimisation">Running an optimisation</ArticleLink>.
          </li>
          <li>
            <strong>Read the results.</strong> Numbers, charts and savings appear on the same page.
            What each KPI means is in <ArticleLink to="results-glossary">the glossary</ArticleLink>;
            how to interpret each chart is in the rest of this section.
          </li>
        </ol>

        <Tip>
          The platform shows clearly-labelled illustrative results if a service is unavailable or
          required data has not been uploaded. If a result looks suspiciously round (e.g. exactly
          100 €/year), check the page for an "example data" notice.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'analysis-forecast',
    category: 'analysis',
    title: 'Running a forecast',
    summary: 'Predict the next hours/days of consumption or generation from a building or community.',
    body: (
      <>
        <p>
          A <strong>forecast</strong> takes your past data and predicts future load or generation.
          Use it to plan operations, to drive a day-ahead optimisation, or simply to see what
          tomorrow — or next quarter — looks like.
        </p>

        <h5 className="mt-3">Before you run it</h5>
        <ul>
          <li>The community has at least one building with a <strong>Building Load Profile</strong>
            uploaded (more history → better forecast — at least 3 months of data is recommended for
            reliable forecasts, ideally a year).</li>
          <li>For <em>solar</em> or <em>wind</em> forecasts, you also need a generation file (community
            PV/Wind profile or the building's rooftop solar). A
            {' '}<ArticleLink to="data-weather">weather file</ArticleLink> is optional but improves
            accuracy by 15–30%.</li>
        </ul>

        <h5 className="mt-3">Step by step</h5>
        <ol>
          <li>Open the community → choose <strong>Forecasting</strong> in the sidebar.</li>
          <li>Pick the <strong>mode</strong> from the two tabs at the top of the page:
            <ul>
              <li><strong>Short-Term (Day-Ahead)</strong> — predicts the next 24 hours from a chosen
                target date (up to 7 days ahead).</li>
              <li><strong>Long-Term (Monthly)</strong> — projects monthly figures over a horizon you
                choose: <em>3 months</em>, <em>6 months</em> or <em>12 months</em>.</li>
            </ul>
          </li>
          <li>Pick the <strong>asset(s)</strong> to forecast: a building's load, the community PV
            generation, or the community wind generation. You can tick more than one.</li>
          <li>Click <strong>Run Forecast</strong> (the button label adapts to how many assets are
            selected). A spinner shows <em>Running forecast…</em> while it works.</li>
          <li>Results appear in the right-hand panel. See
            {' '}<ArticleLink to="results-forecast">Reading the forecast chart</ArticleLink>
            {' '}for what each line and band means.</li>
        </ol>
        <ScreenshotPlaceholder
          img="forecast-page.png"
          description="The Forecasting page — the Short-Term / Long-Term tabs, the asset selector, and the Run Forecast button on the left; the result panel placeholder on the right."
          source="Platform — open any community → click 'Forecasting' in the sidebar. Capture the page before running anything, with both tabs visible."
          caption="The Forecasting page: pick a tab, pick assets, run."
        />

        <Tip>
          If the same forecast looks very different from one run to the next, you likely have very
          little input data. Add more history and re-run. See
          {' '}<ArticleLink to="tb-forecast-flat">"My forecast looks flat or wrong"</ArticleLink>
          {' '}in Troubleshooting if results still seem off.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'analysis-optimisation',
    category: 'analysis',
    title: 'Running an optimisation (Modelling)',
    summary: 'Decide when to import, export, charge or discharge — to minimise cost or maximise self-sufficiency.',
    body: (
      <>
        <p>
          The <strong>Modelling</strong> tool finds the best schedule for your community's flexible
          assets — when to charge or discharge a battery, when to charge EVs, when to import from
          the grid and when to export — under an objective you choose.
        </p>

        <h5 className="mt-3">Before you run it</h5>
        <ul>
          <li>A community with at least one <strong>load</strong> file uploaded.</li>
          <li>A <strong>tariff</strong> file (the optimiser needs prices to know what is cheap and
            what is expensive). See <ArticleLink to="data-tariff">Tariff files</ArticleLink>.</li>
          <li>Optional but recommended: a <strong>generation</strong> file and any
            {' '}<strong>assets</strong> (PV, battery, EV) entered for the community.</li>
        </ul>

        <h5 className="mt-3">Step by step</h5>
        <ol>
          <li>Open the community → choose <strong>Optimization</strong> in the sidebar. (The page
            itself is titled "Modelling" — same place.)</li>
          <li>Choose an <strong>objective</strong>:
            <ul>
              <li><em>Minimise cost</em> — buy when prices are low, sell when prices are high.
                Default for most communities.</li>
              <li><em>Maximise self-sufficiency</em> — use as much of your own generation as
                possible, even if cost is slightly higher.</li>
              <li><em>Maximise self-consumption</em> — minimise exports of your generation.</li>
            </ul>
          </li>
          <li>The optimisation runs a one-day (24-hour) schedule for the next day.</li>
          <li>Review the asset list (PV, battery, EV chargers). Battery settings such as
            {' '}<em>min/max state of charge</em> and <em>round-trip efficiency</em> should match
            reality — change them if not.</li>
          <li>Click <strong>Run Optimization</strong>. A spinner shows <em>Running optimization…</em>
            (you may also see a "Solving the MILP problem" message — that is the mathematical solver
            at work). A run of a few buildings usually finishes in well under a minute.</li>
          <li>When it completes, the result panel appears below — KPI cards on top, hourly Energy
            Balance and battery charts below. See
            {' '}<ArticleLink to="results-optimisation-kpis">Reading the optimisation KPIs</ArticleLink>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="modelling-page.png"
          description="The Modelling page (reached via the Optimization sidebar item) — objective selector (Minimise cost / Maximise self-sufficiency / Maximise self-consumption), the asset list, and the Run Optimization button."
          source="Platform — open any community → click 'Optimization' in the sidebar. (The page is titled 'Modelling'.) Capture before running, showing the objective radios and the asset list."
          caption="The Modelling page: pick an objective, check the assets, run."
        />

        <Warn>
          If the run finishes with "infeasible", the constraints cannot all be satisfied at once
          (for example, the battery is too small for the demand). See
          {' '}<ArticleLink to="tb-optimisation-infeasible">"Optimisation says infeasible"</ArticleLink>.
        </Warn>
      </>
    ),
  },

  // =========================================================================
  // UNDERSTANDING RESULTS
  // =========================================================================
  {
    id: 'results-glossary',
    category: 'results',
    title: 'Glossary — energy terms in plain language',
    summary: 'Every term that appears in result charts and KPI cards, in one place.',
    body: (
      <>
        <p>The other articles in this section link back here. Bookmark it as a quick reference.</p>

        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Term</th><th>What it means</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>kWh</strong> (kilowatt-hour)</td>
              <td>A unit of <em>energy</em>. One kWh ≈ a fan-heater running for one hour. Bills are in kWh.</td>
            </tr>
            <tr>
              <td><strong>kW</strong> (kilowatt)</td>
              <td>A unit of <em>power</em> — how fast energy is being used right now. A 2 kW kettle for half an hour uses 1 kWh.</td>
            </tr>
            <tr>
              <td><strong>Peak demand</strong></td>
              <td>The highest power (kW) drawn in a period. Network fees are often tied to this number.</td>
            </tr>
            <tr>
              <td><strong>Generation</strong></td>
              <td>Electricity produced by your solar panels, wind turbine, etc.</td>
            </tr>
            <tr>
              <td><strong>Load / consumption</strong></td>
              <td>Electricity used by buildings and equipment.</td>
            </tr>
            <tr>
              <td><strong>Self-consumption (%)</strong></td>
              <td>Of all the energy <em>you produced</em>, what share did you use yourself
                (instead of exporting). High = you waste less of your generation.</td>
            </tr>
            <tr>
              <td><strong>Self-sufficiency (%)</strong></td>
              <td>Of all the energy <em>you consumed</em>, what share came from your own generation.
                High = you depend less on the grid.</td>
            </tr>
            <tr>
              <td><strong>Shared energy</strong> <em>(concept)</em></td>
              <td>Energy generated by one community member and consumed within the same hour by
                another member. Many national schemes reward this. The platform does not currently
                show this as a separate KPI — it shows up indirectly through self-consumption and
                the Energy Balance chart.</td>
            </tr>
            <tr>
              <td><strong>Import / export</strong></td>
              <td>Import = electricity drawn from the grid. Export = electricity sent into the grid.</td>
            </tr>
            <tr>
              <td><strong>Tariff</strong></td>
              <td>The price of electricity, in €/kWh, often varying by hour.</td>
            </tr>
            <tr>
              <td><strong>State of charge (SOC)</strong></td>
              <td>How full a battery is, 0% (empty) to 100% (full).</td>
            </tr>
            <tr>
              <td><strong>NPV</strong> (Net Present Value)</td>
              <td>The total value of an investment over its lifetime, in today's money. <strong>Positive = profitable</strong>; negative = loss.</td>
            </tr>
            <tr>
              <td><strong>IRR</strong> (Internal Rate of Return) <em>(concept)</em></td>
              <td>The annual percentage return of the investment. The platform's CBA results
                currently show NPV, BCR, payback, annual savings and CO₂ — not IRR directly — but
                the term appears in the tool's description.</td>
            </tr>
            <tr>
              <td><strong>BCR</strong> (Benefit–Cost Ratio)</td>
              <td>Total benefits divided by total costs. Above 1 = the investment pays for itself.</td>
            </tr>
            <tr>
              <td><strong>Payback (years)</strong></td>
              <td>How many years of savings it takes to cover the initial cost.</td>
            </tr>
            <tr>
              <td><strong>MAPE</strong> (Mean Absolute Percentage Error)</td>
              <td>Forecast error. Lower is better — e.g. a MAPE of 10% means predictions are off by
                10% on average.</td>
            </tr>
            <tr>
              <td><strong>GHI</strong></td>
              <td>Global Horizontal Irradiance — total sunlight hitting a flat surface, in W/m². The main weather input for solar forecasting.</td>
            </tr>
          </tbody>
        </Table>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-forecast',
    category: 'results',
    title: 'Reading the forecast chart',
    summary: 'What the line, the shaded band, and the accuracy number mean.',
    body: (
      <>
        <p>
          A forecast result has three parts: a <strong>history line</strong>, a
          {' '}<strong>predicted line</strong>, and a <strong>shaded band</strong> around the
          prediction showing its uncertainty range.
        </p>
        <ScreenshotPlaceholder
          img="forecast-results.png"
          description="A finished forecast — line for past data, a forecast line, and a shaded band around the prediction. A MAPE value and a Confidence level badge (LOW / MEDIUM / MEDIUM-HIGH / HIGH) are shown alongside."
          source="Platform — Forecasting page, after a successful run. Capture the result panel on the right: chart + the MAPE/Confidence indicators. (A community with uploaded load data is needed.)"
          caption="A typical forecast result."
        />

        <ul>
          <li><strong>History line</strong> — what actually happened. Use it to sanity-check that the input data looks right.</li>
          <li><strong>Forecast line</strong> — the model's best single guess for the future.</li>
          <li><strong>Shaded band</strong> — a range where the true value is likely to fall. A <em>narrow</em> band means the model is confident; a <em>wide</em> band means uncertainty (often the case at long horizons or with little input data).</li>
        </ul>

        <h5 className="mt-3">Two accuracy indicators</h5>
        <p>
          The platform shows two summary indicators next to every forecast — read them together:
        </p>

        <p className="mb-1"><strong>1. Confidence level</strong> — a single badge with one of four values:</p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Badge</th><th>Meaning</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>HIGH</strong></td><td>Plenty of clean input data; results suitable for operational decisions.</td></tr>
            <tr><td><strong>MEDIUM-HIGH</strong></td><td>Good. Usable for planning.</td></tr>
            <tr><td><strong>MEDIUM</strong></td><td>Workable but imperfect input — verify before acting on edge cases.</td></tr>
            <tr><td><strong>LOW</strong></td><td>Limited input data or weak signal. Treat the forecast as indicative; add more history.</td></tr>
          </tbody>
        </Table>

        <p className="mt-3 mb-1"><strong>2. MAPE</strong> (Mean Absolute Percentage Error) — the average gap between prediction and reality, in %. Rough industry guide:</p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>MAPE</th><th>Meaning</th></tr>
          </thead>
          <tbody>
            <tr><td>&lt; 10%</td><td>Very good.</td></tr>
            <tr><td>10 – 20%</td><td>Good. Usable for planning and optimisation.</td></tr>
            <tr><td>20 – 35%</td><td>Acceptable for early stage. Add data or weather to improve.</td></tr>
            <tr><td>&gt; 35%</td><td>Treat as illustrative. See <ArticleLink to="tb-forecast-flat">"My forecast looks flat or wrong"</ArticleLink>.</td></tr>
          </tbody>
        </Table>

        <Tip>
          Solar forecasts are usually more accurate than load forecasts because sunlight is more
          predictable. Adding a weather file pushes solar MAPE down further — see
          {' '}<ArticleLink to="data-weather">Weather files</ArticleLink>.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-optimisation-kpis',
    category: 'results',
    title: 'Reading the optimisation KPIs',
    summary: 'What each card in the KPI Summary means after a Modelling run.',
    body: (
      <>
        <p>
          After an optimisation run, a <strong>KPI Summary</strong> appears above the charts. The
          cards shown depend on whether your community has a battery and whether a tariff was
          available, but the standard set is below.
        </p>
        <ScreenshotPlaceholder
          img="kpi-cards.png"
          description="The KPI Summary panel that appears after running an optimisation — cards for Daily Cost, Self-Sufficiency, Self-Consumption, Grid Import, Grid Import Cost, Grid Export Revenue, Peak Import and CO2 Avoided."
          source="Platform — Optimization page (titled 'Modelling'), after a successful run. Capture the KPI Summary row that appears above the charts."
          caption="The KPI Summary panel from the optimisation result."
        />

        <h5 className="mt-3">Every KPI explained</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>KPI</th><th>What it tells you</th><th>How to improve it</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Daily Cost</strong></td>
              <td>Total cost of running the community for the optimised day (imports minus export revenue).</td>
              <td>Better tariff signals, more PV, a battery sized to the peak hours.</td>
            </tr>
            <tr>
              <td><strong>Self-Sufficiency (%)</strong></td>
              <td>"Of the energy I used, how much came from my own generation?"</td>
              <td>Add more PV; reduce consumption; add storage.</td>
            </tr>
            <tr>
              <td><strong>Self-Consumption (%)</strong></td>
              <td>"Of the energy I produced, how much did I use myself instead of exporting?"</td>
              <td>Add a battery; shift flexible loads to sunny hours.</td>
            </tr>
            <tr>
              <td><strong>Grid Import (kWh)</strong></td>
              <td>Total energy bought from the grid that day.</td>
              <td>More PV and storage; flexible loads at solar hours.</td>
            </tr>
            <tr>
              <td><strong>Grid Import Cost</strong></td>
              <td>What the imports cost in currency. Combines volume × tariff.</td>
              <td>Avoid imports at peak-price hours; pre-charge from cheaper hours.</td>
            </tr>
            <tr>
              <td><strong>Grid Export Revenue</strong></td>
              <td>What you were paid for exports.</td>
              <td>Depends on your export tariff scheme; not a goal in itself if self-consumption is preferred.</td>
            </tr>
            <tr>
              <td><strong>Peak Import (kW)</strong></td>
              <td>Highest hour-by-hour grid draw during the day. Network fees are often tied to this.</td>
              <td>Spread loads in time; use a battery for peak shaving.</td>
            </tr>
            <tr>
              <td><strong>CO2 Avoided (kg)</strong></td>
              <td>Emissions avoided vs taking the same energy from the grid. Driven by self-consumed PV/wind.</td>
              <td>Maximise self-consumption; size PV to the daytime load.</td>
            </tr>
          </tbody>
        </Table>

        <h5 className="mt-3">Self-consumption vs Self-sufficiency — the most confused pair</h5>
        <p>They sound alike but answer opposite questions:</p>
        <ul>
          <li><strong>Self-consumption</strong> is about <em>your generation</em>: how much of what you made did you use?</li>
          <li><strong>Self-sufficiency</strong> is about <em>your consumption</em>: how much of what you used did you make yourself?</li>
        </ul>

        <h5 className="mt-3">Sense-checking ranges</h5>
        <ul>
          <li>A community with only modest rooftop PV and no battery: self-consumption 60–90%, self-sufficiency 10–25%.</li>
          <li>A community with a large solar farm and a battery: self-sufficiency can exceed 50%, self-consumption 80–95%.</li>
          <li>Self-sufficiency above ~90% means you barely import — verify the load file is not understated.</li>
        </ul>
        <Warn>
          If a KPI looks unrealistically good (e.g. 99% self-sufficiency with only modest PV), see
          {' '}<ArticleLink to="tb-kpi-sanity-check">"My KPIs look too good or too bad"</ArticleLink>.
        </Warn>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-energy-flow',
    category: 'results',
    title: 'Reading the Energy Balance chart',
    summary: 'How to read the hour-by-hour chart that shows every source and use of energy after an optimisation.',
    body: (
      <>
        <p>
          The <strong>Energy Balance</strong> chart appears under the KPI Summary on the Modelling
          (Optimization) results. It is an hourly area chart with one series for each kind of source
          or use, switchable between community-wide and per-building views.
        </p>
        <ScreenshotPlaceholder
          img="energy-flow.png"
          description="The Energy Balance area chart over 24 hours — stacked series for Base Load, Flexible Load, PV Generation (or Rooftop PV), Wind Generation, Grid Import, Surplus, Battery Charge and Battery Discharge."
          source="Platform — Optimization page (titled 'Modelling'), after a successful run. Scroll below the KPI Summary to the Energy Balance chart and capture the full 24-hour area chart."
          caption="Energy Balance across the optimised day."
        />

        <h5 className="mt-3">The series you'll see</h5>
        <p>The chart shows up to these series — only the ones relevant to your assets appear:</p>
        <ul>
          <li><strong>Base Load</strong> — the fixed consumption that has to be met every hour.</li>
          <li><strong>Flexible Load</strong> — consumption the optimiser is allowed to shift in time.</li>
          <li><strong>PV Generation</strong> (community) or <strong>Rooftop PV</strong> (per building).</li>
          <li><strong>Community Generation Allocation</strong> — share of community generation routed to this building (per-building view only).</li>
          <li><strong>Wind Generation</strong> — output of any community wind turbine.</li>
          <li><strong>Grid Import</strong> — energy taken from the grid that hour.</li>
          <li><strong>Surplus</strong> — generation that was neither consumed nor stored that hour (exported, or curtailed).</li>
          <li><strong>Battery Charge</strong> and <strong>Battery Discharge</strong> — the battery's contribution per hour.</li>
        </ul>

        <h5 className="mt-3">Patterns that tell you something</h5>
        <ul>
          <li><strong>Tall PV in the middle of the day + a Surplus block</strong> — solar produced more than the community could use; without a bigger battery, the rest is exported or curtailed.</li>
          <li><strong>PV at midday + Battery Charge at the same hour</strong> — the optimiser is saving surplus for the evening peak.</li>
          <li><strong>Battery Discharge in the evening + Grid Import staying small</strong> — the battery is doing its job; little expensive grid energy is needed at peak prices.</li>
          <li><strong>Grid Import overnight + Battery Charge at the same time</strong> — only sensible if night tariffs are very cheap; otherwise check the tariff file.</li>
        </ul>
        <Tip>Cross-check with the <ArticleLink to="results-battery">battery state-of-charge curve</ArticleLink> — they tell the same story from two angles.</Tip>

        <h5 className="mt-3">Annual Energy Flows (Long-Term Planning)</h5>
        <p>
          A different chart with a similar name appears in <em>Long-Term Planning</em> results — the
          <strong> Annual Energy Flows</strong> bar chart. There, each bar is one year of the
          projection: positive values = energy consumed or imported, negative values = energy
          exported to the grid. Use it to see how the energy mix shifts over the lifetime of the
          investment.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-battery',
    category: 'results',
    title: 'Reading the battery results',
    summary: 'What the state-of-charge curve and the battery charge/discharge series tell you.',
    body: (
      <>
        <p>
          When a battery is part of your community, the optimiser produces a
          {' '}<strong>Battery Schedule</strong> view with the <strong>state-of-charge (SOC)
          curve</strong> showing how full the battery is in each hour, plus the matching charge and
          discharge series in the <ArticleLink to="results-energy-flow">Energy Balance chart</ArticleLink>.
        </p>
        <ScreenshotPlaceholder
          img="battery-soc.png"
          description="The state-of-charge curve — 0% to 100% on the Y axis, hours on the X axis, typically a daily up-down pattern between the configured Min SOC and Max SOC bounds (defaults 10% and 90%)."
          source="Platform — Optimization page (titled 'Modelling'), after a successful run on a community that has a battery asset. Scroll to the Battery Schedule section and capture the SOC line chart."
          caption="State of charge across the optimised day."
        />

        <h5 className="mt-3">A healthy daily pattern</h5>
        <ul>
          <li>SOC rises during sunny hours (charging from PV) or during cheap-price hours.</li>
          <li>SOC falls in the evening peak (discharging into loads, displacing expensive grid imports).</li>
          <li>SOC stays inside the configured bounds — defaults are <strong>Min 10%</strong> and
            <strong> Max 90%</strong>, set on the battery asset.</li>
          <li>The curve should not sit flat at the minimum or maximum for many hours — that
            suggests the battery is either too small or under-used.</li>
        </ul>

        <h5 className="mt-3">Battery settings to check (on the asset)</h5>
        <p>
          The battery's behaviour in the optimisation is shaped by these settings on the community
          or building asset page:
        </p>
        <ul>
          <li><strong>Capacity (kWh)</strong> — how much energy fits inside.</li>
          <li><strong>Charge / discharge rate (kW)</strong> — how fast it can move energy in and out.</li>
          <li><strong>Round-trip efficiency (%)</strong> — energy out divided by energy in. Default
            is 90%. Realistic real-world Li-ion is 85–95% — anything much lower means losses are
            being modelled aggressively.</li>
          <li><strong>Min / Max SOC (%)</strong> — the operating window, default 10% to 90%.</li>
          <li><strong>Initial SOC (%)</strong> — where the battery starts (default 50%).</li>
        </ul>

        <h5 className="mt-3">Reading the schedule alongside the SOC</h5>
        <p>
          Compare the SOC curve with the Battery Charge / Battery Discharge series in the
          {' '}<ArticleLink to="results-energy-flow">Energy Balance chart</ArticleLink>: each hour
          of "charge" should push SOC up, each hour of "discharge" should pull it down. A jump in
          SOC without a matching charge in the schedule means the data was edited between views and
          should not happen in a single run.
        </p>
        <Warn>
          A battery that sits at 100% (or its Max SOC) for many hours is doing nothing — either the
          PV is oversized relative to load, the tariff has no useful price spread, or the
          objective/constraints prevent discharge. Try the <em>Minimise cost</em> objective with a
          real day-ahead tariff to see if the battery becomes more active.
        </Warn>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-cba',
    category: 'results',
    title: 'Reading the Long-Term Planning (CBA) results',
    summary: 'NPV, BCR, payback, annual savings, CO₂ — what each number means and how to compare scenarios.',
    body: (
      <>
        <p>
          <strong>Long-Term Planning</strong> compares one or more investment scenarios (new PV,
          new battery, new EV chargers, etc.) against the <em>baseline</em> of doing nothing,
          over a horizon you choose (typically 5, 10 or 20 years). The result is a side-by-side
          table of financial KPIs plus charts.
        </p>
        <ScreenshotPlaceholder
          img="cba-scenarios.png"
          description="The CBA scenario comparison — one row per scenario showing NPV, BCR, payback, annual savings, and CO₂ avoided per year. The best-financial scenario is highlighted, and the best-environmental scenario (if different) is badged."
          source="Platform — open a community → click 'Past Results' (or Planning Tools) → run a Long-Term Planning scenario → open its results. Capture the scenario comparison panel near the top of the page."
          caption="Scenario comparison from Long-Term Planning."
        />

        <h5 className="mt-3">What each column means</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Column</th><th>Plain-language meaning</th><th>Good if…</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>NPV</strong></td><td>Total profit (in today's money) over the project lifetime, after applying the discount rate.</td><td>Positive (and as large as possible).</td></tr>
            <tr><td><strong>BCR</strong></td><td>Benefit–Cost Ratio: total benefits divided by total costs.</td><td>Above 1.0.</td></tr>
            <tr><td><strong>Payback (years)</strong></td><td>Years before cumulative savings cover the upfront cost.</td><td>Shorter than the asset's lifetime (e.g. solar lasts 20–25 years).</td></tr>
            <tr><td><strong>Annual savings</strong></td><td>Average savings per year vs the baseline (or annual grid cost for the baseline row).</td><td>The bigger the better — but compare against the upfront cost.</td></tr>
            <tr><td><strong>CO₂ avoided / year</strong></td><td>Tonnes of CO₂ not emitted per year vs the baseline (uses your community's grid CO₂ factor).</td><td>Bigger is better; useful for sustainability reporting.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-3">A note on the discount rate</h5>
        <p>
          The page exposes a <strong>Discount Rate</strong> input (default 8%) and a sensitivity
          table. NPV is calculated for the rate you set. Try moving it ±2 percentage points to see
          how sensitive your conclusion is — see the <em>Discount-rate sensitivity</em> table on
          the same page.
        </p>

        <h5 className="mt-3">How to compare scenarios</h5>
        <ol>
          <li>Look at <strong>NPV</strong> first — it is the single best financial summary. The scenario with the highest NPV is the financial winner and is highlighted on the page.</li>
          <li>Sanity-check with <strong>BCR</strong> and <strong>payback</strong> — if NPV is high but payback is 18 years on an asset that lasts 20, the case is fragile to assumptions.</li>
          <li>If two scenarios tie on NPV, prefer the higher BCR or the lower upfront cost.</li>
          <li>Compare <strong>CO₂ avoided</strong> separately — the best-environmental scenario can differ from the best-financial one, and the page badges it accordingly.</li>
        </ol>

        <Warn>
          A positive NPV depends on the input assumptions — electricity-price inflation, discount
          rate, asset lifetime. Use the sensitivity table on the page to see if the NPV stays
          positive under a modest change in the discount rate; if it flips to negative easily, the
          case is not robust.
        </Warn>
        <Tip>
          The platform's results page shows the best-financial and best-environmental scenarios as
          summary callouts. Use those as a quick read, but always inspect the full table before
          recommending an investment.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'tb-upload-errors',
    category: 'troubleshooting',
    title: 'Why did my upload fail? — error messages explained',
    summary: 'Every red error and yellow warning, with the cause and the fix.',
    body: (
      <>
        <p>
          When an upload is rejected you see a <strong>red</strong> banner and a list of problems.
          <strong> Yellow</strong> messages are warnings only — the file is still accepted. Find
          your message below.
        </p>

        <h5 className="mt-3">Red errors (file rejected)</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Message</th><th>What it means &amp; how to fix it</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><Code>Missing required column: "..."</Code></td>
              <td>The heading row does not contain a column the file needs — usually a spelling or
                capitalisation difference. Headings must match exactly, e.g. <Code>consumption_kwh</Code>,
                all lower-case. Fix the first row and re-upload.</td>
            </tr>
            <tr>
              <td><Code>Value must be a number, got: "..."</Code></td>
              <td>A cell that should hold a number contains something else — almost always a
                <strong> decimal comma</strong> (<Code>3,5</Code>); it must be a dot (<Code>3.5</Code>).
                To convert a whole file at once, see <ArticleLink to="data-fix-commas">Fixing a file
                that uses commas for decimals</ArticleLink>. Also remove units, spaces or other text
                from number cells.</td>
            </tr>
            <tr>
              <td><Code>Value must be non-negative, got: ...</Code></td>
              <td>A consumption, generation or price value is negative. These cannot be below zero —
                a negative number usually means two columns were swapped.</td>
            </tr>
            <tr>
              <td><Code>Invalid timestamp format: "..."</Code></td>
              <td>A date/time cell could not be read. Use <Code>2025-01-01T14:00:00Z</Code>.
                See <ArticleLink to="data-csv-basics">Creating CSV files correctly</ArticleLink> for
                the accepted date formats and a formula that fixes a whole column at once.</td>
            </tr>
            <tr>
              <td><Code>CSV file contains no data rows</Code></td>
              <td>The file has only a heading row, or is empty. Add your hourly data rows.</td>
            </tr>
            <tr>
              <td><Code>period_start must be before period_end</Code></td>
              <td>(Energy bill files) A bill's start date is the same as or later than its end date. Correct the dates.</td>
            </tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Yellow warnings (file still accepted)</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Message</th><th>What it means</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>Extra columns found and will be ignored</Code></td><td>Your file has columns the platform does not use. Harmless — they are simply skipped.</td></tr>
            <tr><td><Code>... gap(s) detected in the time series</Code></td><td>Some hours are missing between your first and last row. Add the missing hours for best accuracy.</td></tr>
            <tr><td><Code>timestamp(s) are not in chronological order</Code></td><td>Rows are not sorted by time. Sort the file by the timestamp column.</td></tr>
            <tr><td><Code>has ... missing value(s)</Code></td><td>Some cells in a number column are blank. Fill them in (use <Code>0</Code> where appropriate).</td></tr>
            <tr><td><Code>... potential outliers</Code></td><td>A few values are unusually high or low. Check them in case of a typo, otherwise ignore.</td></tr>
            <tr><td><Code>contains all zero values</Code></td><td>A whole column is zero. Fine for night-time solar; otherwise check you exported the right data.</td></tr>
          </tbody>
        </Table>
        <Tip>
          Most red errors come from the decimal comma or from mis-typed headings. Starting from a
          downloaded template avoids both — see <em>“Creating CSV files correctly”</em>.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'tb-forecast-flat',
    category: 'troubleshooting',
    title: 'My forecast looks flat or wrong',
    summary: 'When the predicted line is a flat line, a copy of yesterday, or wildly off — what to check.',
    body: (
      <>
        <p>If a forecast looks unhelpful, almost always one of these causes is at play.</p>

        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>What you see</th><th>Most likely cause</th><th>What to do</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Flat line</strong> — prediction is a single value across the whole horizon.</td>
              <td>Very little input history, or the input is mostly zeros / a single repeated value.</td>
              <td>Upload more history (at least one month, ideally a year). Check the file isn't a single repeated value.</td>
            </tr>
            <tr>
              <td><strong>"Copy of yesterday"</strong> — prediction looks like the last 24 h, shifted.</td>
              <td>The model has fallen back to a naïve baseline because the data is too short to fit a real model.</td>
              <td>Add more history. Add a weather file for solar forecasts (see <ArticleLink to="data-weather">Weather files</ArticleLink>).</td>
            </tr>
            <tr>
              <td><strong>Spikes / negative values</strong> in the prediction.</td>
              <td>Outliers in the history (e.g. a meter glitch) are dragging the model.</td>
              <td>Open the history file, find the suspicious row, correct or remove it, re-upload, re-run.</td>
            </tr>
            <tr>
              <td><strong>Very wide confidence band</strong>.</td>
              <td>The model knows it is uncertain — usually short history, or unusual recent data.</td>
              <td>Add more data; expect the band to narrow on the next run.</td>
            </tr>
            <tr>
              <td><strong>"Sample data" notice on the page</strong>.</td>
              <td>The forecast service was unavailable or the data was insufficient — illustrative results are shown.</td>
              <td>Check the upload page for missing files; try again in a few minutes.</td>
            </tr>
            <tr>
              <td><strong>Solar forecast is roughly right but the magnitude is off</strong>.</td>
              <td>PV capacity / panel orientation may be set wrong on the asset, or the generation file uses kWh where kW is expected (or vice versa).</td>
              <td>Verify the asset's installed capacity (kWp). Verify the unit of the generation file matches what the platform expects on the upload page.</td>
            </tr>
          </tbody>
        </Table>

        <Tip>
          A useful gut-check: if you forecast a building's <em>past</em> day and the prediction
          looks nothing like what happened, the model is under-served. Adding history is almost
          always more effective than tweaking settings.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'tb-optimisation-infeasible',
    category: 'troubleshooting',
    title: 'Optimisation says "infeasible"',
    summary: 'When the Modelling run fails to find any valid schedule — common causes and fixes.',
    body: (
      <>
        <p>
          "Infeasible" means the optimiser could not find <em>any</em> schedule that satisfies every
          constraint at the same time. Usually one constraint is too tight.
        </p>

        <h5 className="mt-3">Things to check, in order</h5>
        <ol>
          <li>
            <strong>Battery capacity vs demand.</strong> If you forced the battery to cover the
            evening peak but its capacity is too small, the optimiser has no valid plan. Increase
            the battery capacity, or relax the constraint.
          </li>
          <li>
            <strong>EV plug-in window vs energy required.</strong> If an EV needs 40 kWh but is only
            plugged in for 2 hours at 7 kW (=14 kWh maximum), it cannot be filled. Widen the window
            or reduce the required SOC.
          </li>
          <li>
            <strong>Self-sufficiency target vs generation.</strong> A target of "100% self-sufficient"
            with little PV and no grid import allowed is impossible. Lower the target or allow some
            import.
          </li>
          <li>
            <strong>Min/Max SOC bounds.</strong> If the minimum SOC is higher than the maximum SOC
            (or both pinned at the same level), there is no room to operate. Check both bounds.
          </li>
          <li>
            <strong>Negative tariff prices.</strong> Some exports show negative wholesale prices in
            real markets. If you set an "export ≥ 0" rule, those hours become infeasible. Remove
            the rule, or pre-filter the tariff file.
          </li>
          <li>
            <strong>Missing data within the horizon.</strong> A gap in the load or tariff file
            inside the optimisation horizon can break the solver. Re-upload the file with the gap
            filled (use the previous hour's value or 0).
          </li>
        </ol>

        <h5 className="mt-3">How to diagnose quickly</h5>
        <p>
          Re-run with the <strong>"Minimise cost"</strong> objective and <em>no</em> hard
          self-sufficiency / self-consumption target. If that succeeds, the binding constraint was
          your target — relax it. If it still fails, the binding constraint is asset capacity or a
          data gap.
        </p>
        <Tip>
          Halving the horizon (24 h → 12 h) sometimes succeeds when 24 h does not. That points to a
          specific time block where the problem sits — inspect that block in your data.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'tb-kpi-sanity-check',
    category: 'troubleshooting',
    title: 'My KPIs look too good — or too bad',
    summary: 'When self-sufficiency is 99%, NPV is huge, or payback is implausibly short — what to check.',
    body: (
      <>
        <p>
          A KPI that looks "too good to be true" almost always comes from one of a small set of data
          mistakes. Run through these checks before sharing the result.
        </p>

        <h5 className="mt-3">If self-sufficiency or self-consumption is implausibly high</h5>
        <ul>
          <li><strong>Consumption file scaled too small.</strong> kW values entered into a kWh column (or vice versa) make the community look tiny vs its PV. Re-check the unit on the upload page.</li>
          <li><strong>Only a partial year uploaded.</strong> If you uploaded the summer months only, of course solar covers most of the demand. Upload a full year for a representative figure.</li>
          <li><strong>Missing buildings.</strong> Only one of three buildings is in the platform, so the community looks smaller and more solar-covered than reality.</li>
        </ul>

        <h5 className="mt-3">If NPV or annual savings is implausibly large</h5>
        <ul>
          <li><strong>Tariff in the wrong units.</strong> €/MWh treated as €/kWh inflates savings by 1000×. Open the tariff file — a price of <Code>120</Code> should be <Code>0.12</Code> if it represents €/kWh.</li>
          <li><strong>Lifetime too long.</strong> A 30-year horizon on a 15-year battery overstates savings. Match the lifetime to the asset.</li>
          <li><strong>Discount rate set to 0%.</strong> No discounting means future savings count fully — usually unrealistic. Use 3–6% unless your finance team says otherwise.</li>
          <li><strong>Inflation assumption too aggressive.</strong> If you assumed electricity prices grow 8% per year forever, savings explode. Use a sober figure (1–4%).</li>
        </ul>

        <h5 className="mt-3">If results look implausibly bad</h5>
        <ul>
          <li><strong>Tariff file is missing or all zeros.</strong> No price = no savings to count. Upload the tariff and re-run.</li>
          <li><strong>PV capacity left at 0 kWp.</strong> Check the asset on the community's assets page.</li>
          <li><strong>Wrong battery efficiency.</strong> A default of 50% would cause it to "lose" half of every kWh — check the asset's round-trip efficiency.</li>
          <li><strong>Generation file in the wrong column.</strong> If consumption and generation got swapped, the optimiser sees the building producing nothing.</li>
        </ul>

        <Tip>
          A useful sanity check: divide annual savings by the community's annual electricity bill.
          If the result is more than 50–70%, something is probably overstated. Real-world community
          energy projects typically cut bills by 10–40%.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'tb-faq',
    category: 'troubleshooting',
    title: 'Frequently asked questions',
    summary: 'Short answers to common questions.',
    body: (
      <>
        <h5>Do I need a full year of data?</h5>
        <p>A full year (8,760 hourly rows) gives the best results, but the tools work with less.
          At least one day (24 rows) is required for time-based files.</p>

        <h5 className="mt-3">My file uses a semicolon ( ; ) between values — is that a problem?</h5>
        <p>No. The platform automatically detects commas, semicolons and tabs. The decimal
          separator inside numbers still has to be a dot.</p>

        <h5 className="mt-3">Can I upload the same file type again?</h5>
        <p>Yes — uploading again replaces the previous file for that type (for day-ahead tariffs,
          each day's file is kept separately by date).</p>

        <h5 className="mt-3">The results look like sample data — why?</h5>
        <p>If a calculation service is temporarily unavailable, or required data has not been
          uploaded yet, the platform shows clearly-labelled illustrative results so you can still
          explore. Upload real data and run the tool again for true results.</p>

        <h5 className="mt-3">Why is a button greyed out?</h5>
        <p>Some accounts are read-only (viewer role), and some actions need data to be uploaded
          first. Hover over the button or check the page hints to see what is required.</p>
      </>
    ),
  },
]
